import { z } from 'zod';

import { CatalogGenericProductResponseSchema } from './catalog-generic-product.schemas.js';

/** ADR-0044 / BR-PRD-28 — curadoria do catálogo global ZR (escopo SYSTEM). */

export const CatalogCurationStatusSchema = z.enum(['UNVERIFIED', 'VERIFIED', 'MERGED']);

export const CatalogProductIdParamsSchema = z.looseObject({
  id: z.uuid(),
});

export const CatalogProductAttributeValueInputSchema = z.looseObject({
  attributeKey: z.string().trim().min(1).max(80),
  value: z.union([z.string(), z.number(), z.boolean()]),
});

export const UpdateCatalogProductCanonicalBodySchema = z.looseObject({
  canonicalName: z.string().trim().min(1).max(200).optional(),
  ean: z.string().trim().min(1).max(14).nullable().optional(),
  brandId: z.uuid().nullable().optional(),
  brandName: z.string().trim().min(1).max(120).nullable().optional(),
  mpn: z.string().trim().min(1).max(60).nullable().optional(),
  packageBarcode: z.string().trim().min(1).max(64).nullable().optional(),
  grossWeightGrams: z.number().int().min(0).nullable().optional(),
  netWeightGrams: z.number().int().min(0).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  genericProductId: z.uuid().optional(),
  attributes: z.array(CatalogProductAttributeValueInputSchema).optional(),
});

/** BR-PRD-100 / BR-PRD-101 — cadastro direto ZR a partir do genérico. */
export const RegisterCatalogProductDirectBodySchema = z.looseObject({
  genericProductId: z.uuid().nullable().optional(),
  canonicalName: z.string().trim().min(1).max(200),
  ean: z.string().trim().min(1).max(14).nullable().optional(),
  brandId: z.uuid().nullable().optional(),
  brandName: z.string().trim().min(1).max(120).nullable().optional(),
  mpn: z.string().trim().min(1).max(60).nullable().optional(),
  packageBarcode: z.string().trim().min(1).max(64).nullable().optional(),
  grossWeightGrams: z.number().int().min(0).nullable().optional(),
  netWeightGrams: z.number().int().min(0).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  attributes: z.array(CatalogProductAttributeValueInputSchema).optional(),
});

export const MergeCatalogProductBodySchema = z.looseObject({
  targetId: z.uuid(),
});

export const CatalogProductIdentityFilterSchema = z.enum(['EAN', 'BRAND_MPN']);

/** BR-PRD-31 — `q` casa nome canônico, EAN, marca, MPN e nomes locais dos overlays. */
export const ListCatalogProductsQuerySchema = z.looseObject({
  status: CatalogCurationStatusSchema.optional(),
  q: z.string().trim().min(1).max(200).optional(),
  identity: CatalogProductIdentityFilterSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const CatalogProductResponseSchema = z.looseObject({
  id: z.uuid(),
  canonicalName: z.string(),
  ean: z.string().nullable(),
  brandName: z.string().nullable(),
  mpn: z.string().nullable(),
  status: CatalogCurationStatusSchema,
  mergedIntoId: z.uuid().nullable(),
  originTenantId: z.uuid().nullable(),
  genericProductId: z.uuid().nullable(),
  packageBarcode: z.string().nullable(),
  grossWeightGrams: z.number().int().nullable(),
  netWeightGrams: z.number().int().nullable(),
  notes: z.string().nullable(),
  fiscalDivergent: z.boolean(),
  verifiedAt: z.iso.datetime().nullable(),
  mergedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const LinkCatalogProductGenericBodySchema = z.looseObject({
  genericProductId: z.uuid(),
});

export const PutCatalogProductAttributeValuesBodySchema = z.looseObject({
  values: z.array(CatalogProductAttributeValueInputSchema),
});

export const CatalogProductAttributeValueSchema = z.looseObject({
  attributeKey: z.string(),
  valueText: z.string(),
});

export const CatalogProductAttributeValuesResponseSchema = z.looseObject({
  catalogProductId: z.uuid(),
  values: z.array(CatalogProductAttributeValueSchema),
});

export const CatalogProductDerivedSimilarSchema = z.looseObject({
  id: z.uuid(),
  canonicalName: z.string(),
  status: CatalogCurationStatusSchema,
});

/** BR-PRD-31 — item da listagem inclui contagem de tenants vinculados. */
export const CatalogProductListItemSchema = CatalogProductResponseSchema.extend({
  linksCount: z.number().int().nonnegative(),
});

export const CatalogProductListResponseSchema = z.looseObject({
  items: z.array(CatalogProductListItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
    total: z.number().int().optional(),
  }),
});

/** BR-PRD-31 — KPIs da base global. */
export const CatalogGlobalStatsResponseSchema = z.looseObject({
  total: z.number().int().nonnegative(),
  unverified: z.number().int().nonnegative(),
  verified: z.number().int().nonnegative(),
  merged: z.number().int().nonnegative(),
  feedingTenants: z.number().int().nonnegative(),
});

/** BR-PRD-32 — evidência por tenant, sem dados comerciais. */
export const CatalogProductTenantEvidenceItemSchema = z.looseObject({
  tenantId: z.uuid(),
  tenantName: z.string(),
  localName: z.string(),
  linkedAt: z.iso.datetime(),
});

export const CatalogProductTenantEvidenceResponseSchema = z.looseObject({
  items: z.array(CatalogProductTenantEvidenceItemSchema),
});

/** BR-PRD-33 — sugestão de duplicata por similaridade; null quando não há. */
export const CatalogProductDupeSuggestionResponseSchema = z.looseObject({
  suggestion: CatalogProductResponseSchema.extend({
    similarity: z.number().min(0).max(1),
  }).nullable(),
});

export const DismissCatalogProductDupeBodySchema = z.looseObject({
  suggestedId: z.uuid(),
});

export const DismissCatalogProductDupeResponseSchema = z.looseObject({
  dismissed: z.literal(true),
});

export const CatalogProductMergeTargetSchema = z.looseObject({
  id: z.uuid(),
  canonicalName: z.string(),
  status: CatalogCurationStatusSchema,
});

/** BR-PRD-28 / BR-PRD-73 — detalhe com alvo de mescla, genérico e atributos. */
export const CatalogProductDetailResponseSchema = CatalogProductResponseSchema.extend({
  mergeTarget: CatalogProductMergeTargetSchema.nullable(),
  generic: CatalogGenericProductResponseSchema.nullable(),
  attributes: z.array(CatalogProductAttributeValueSchema),
  derivedSimilars: z.array(CatalogProductDerivedSimilarSchema),
});

export const CatalogManufacturerIdParamsSchema = z.looseObject({
  id: z.uuid(),
});

export const CreateCatalogManufacturerBodySchema = z.looseObject({
  name: z.string().trim().min(1).max(120),
  logoUrl: z.string().trim().min(1).max(500).nullable().optional(),
});

export const UpdateCatalogManufacturerBodySchema = z.looseObject({
  name: z.string().trim().min(1).max(120).optional(),
  logoUrl: z.string().trim().min(1).max(500).nullable().optional(),
});

export const CatalogManufacturerResponseSchema = z.looseObject({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  logoUrl: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  slugChanged: z.boolean().optional(),
});

export type CatalogCurationStatus = z.infer<typeof CatalogCurationStatusSchema>;
export type CatalogProductIdParams = z.infer<typeof CatalogProductIdParamsSchema>;
export type RegisterCatalogProductDirectBody = z.infer<
  typeof RegisterCatalogProductDirectBodySchema
>;
export type UpdateCatalogProductCanonicalBody = z.infer<
  typeof UpdateCatalogProductCanonicalBodySchema
>;
export type CatalogManufacturerIdParams = z.infer<typeof CatalogManufacturerIdParamsSchema>;
export type CreateCatalogManufacturerBody = z.infer<typeof CreateCatalogManufacturerBodySchema>;
export type UpdateCatalogManufacturerBody = z.infer<typeof UpdateCatalogManufacturerBodySchema>;
export type CatalogManufacturerResponse = z.infer<typeof CatalogManufacturerResponseSchema>;
export type MergeCatalogProductBody = z.infer<typeof MergeCatalogProductBodySchema>;
export type ListCatalogProductsQuery = z.infer<typeof ListCatalogProductsQuerySchema>;
export type CatalogProductIdentityFilter = z.infer<typeof CatalogProductIdentityFilterSchema>;
export type CatalogProductResponse = z.infer<typeof CatalogProductResponseSchema>;
export type CatalogProductListItem = z.infer<typeof CatalogProductListItemSchema>;
export type CatalogProductListResponse = z.infer<typeof CatalogProductListResponseSchema>;
export type CatalogGlobalStatsResponse = z.infer<typeof CatalogGlobalStatsResponseSchema>;
export type CatalogProductTenantEvidenceItem = z.infer<
  typeof CatalogProductTenantEvidenceItemSchema
>;
export type CatalogProductTenantEvidenceResponse = z.infer<
  typeof CatalogProductTenantEvidenceResponseSchema
>;
export type CatalogProductDupeSuggestionResponse = z.infer<
  typeof CatalogProductDupeSuggestionResponseSchema
>;
export type DismissCatalogProductDupeBody = z.infer<typeof DismissCatalogProductDupeBodySchema>;
export type DismissCatalogProductDupeResponse = z.infer<
  typeof DismissCatalogProductDupeResponseSchema
>;
export type CatalogProductDetailResponse = z.infer<typeof CatalogProductDetailResponseSchema>;
export type CatalogProductMergeTarget = z.infer<typeof CatalogProductMergeTargetSchema>;
export type LinkCatalogProductGenericBody = z.infer<typeof LinkCatalogProductGenericBodySchema>;
export type PutCatalogProductAttributeValuesBody = z.infer<
  typeof PutCatalogProductAttributeValuesBodySchema
>;
export type CatalogProductAttributeValuesResponse = z.infer<
  typeof CatalogProductAttributeValuesResponseSchema
>;
export type CatalogProductAttributeValue = z.infer<typeof CatalogProductAttributeValueSchema>;
export type CatalogProductDerivedSimilar = z.infer<typeof CatalogProductDerivedSimilarSchema>;
